import {
  DataConnectionId,
  DataConnectionType,
  OrgRole,
  TableType,
  orgRoleHasEditPermissions,
} from "@hex/common";
import React, { useCallback } from "react";

import {
  useCreateExploreFromSemanticResourceCallback,
  useCreateExploreFromWarehouseTable,
} from "../../../explore-v2/useCreateExplore.js";
import { HexButton } from "../../../hex-components/HexButton.js";
import {
  HexMenu,
  HexMenuDivider,
  HexMenuItem,
} from "../../../hex-components/HexMenuItem.js";
import { HexPopover } from "../../../hex-components/HexPopover.js";
import { useCurrentUser } from "../../../hooks/me/useCurrentUser.js";
import {
  DataBrowserMenuItems,
  getDataBrowserMenuText,
} from "../../data/data-browser-modal/DataBrowserMenuItems.js";
import { useFeatureGates } from "../../feature-gate/FeatureGateContext.js";
import { FeatureGateToolTip } from "../../feature-gate/FeatureGateToolTip.js";
import { Heading } from "../../Heading.js";
import {
  ChartCellIcon,
  ExploreIcon,
  MoreMenuIcon,
  SQLCellIcon,
} from "../../icons/CustomIcons.js";
import { TableListFragment } from "../../sidebar/data-browser/DataBrowserTableList.generated.js";
import {
  SemanticDatasetListFragment,
  SemanticViewListFragment,
} from "../../sidebar/data-browser/RecentlyUsedView.generated.js";
import {
  useHomeTableActions,
  useHomeTableSemanticResourceActions,
} from "../data-tab/useHomeTableActions.js";

export interface DataBentoActionsButtonProps {
  className?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  table: TableListFragment;
  connectionType: DataConnectionType;
  additionalMenuItem?: React.ReactNode;
  multiDatabase: boolean;
}

export const DataBentoActionsButton: React.ComponentType<DataBentoActionsButtonProps> =
  React.memo(function DataBentoActionsButton({
    additionalMenuItem,
    className,
    connectionType,
    isOpen,
    multiDatabase,
    setIsOpen,
    table,
  }: DataBentoActionsButtonProps) {
    const {
      createChartCell,
      createDisplayTableCell,
      createPivotCell,
      createSqlCell,
    } = useHomeTableActions({
      browserType: "HOME_DATA_BENTO",
      connectionType,
      dataConnectionId: table.dataConnectionId,
      databaseName: table.dataSourceSchema.dataSourceDatabase.name,
      multiDatabase,
      schemaName: table.dataSourceSchema.name,
      tab: "RECENTLY_USED",
      tableId: table.id,
      tableName: table.name,
      tableType: table.tableType,
    });

    const { exploreFromScratch: exploreFromScratchFeatureGate } =
      useFeatureGates();

    const isMetricView = table.tableType === TableType.METRIC_VIEW;

    const createExplore = useCreateExploreFromWarehouseTable(table.id, {
      browserType: "HOME_DATA_BENTO",
      dataConnectionId: table.dataConnectionId ?? null,
      dataBrowserTab: "RECENTLY_USED",
    });

    const onButtonClick = useCallback(
      (e: React.SyntheticEvent) => {
        setIsOpen(true);
        // prevent triggering data bento row click
        e.stopPropagation();
      },
      [setIsOpen],
    );

    const onPopoverInteraction = useCallback(
      (newIsOpen: boolean, e?: React.SyntheticEvent) => {
        setIsOpen(newIsOpen);
        // prevent event from bubbling up to the parent and
        // triggering data bento row click
        e?.stopPropagation();
      },
      [setIsOpen],
    );

    const orgRole = useCurrentUser()?.orgRole;
    const canEditProjectsAndCollections =
      orgRole !== OrgRole.GUEST && orgRoleHasEditPermissions(orgRole);

    return (
      <HexPopover
        captureDismiss={true}
        content={
          <HexMenu>
            {additionalMenuItem}
            {additionalMenuItem && <HexMenuDivider />}
            {!isMetricView && (
              <FeatureGateToolTip
                action="explore outside of a published app"
                disabled={exploreFromScratchFeatureGate}
                featureGate="exploreFromScratch"
              >
                <HexMenuItem
                  icon={<ExploreIcon />}
                  text="Explore"
                  onClick={() => createExplore()}
                />
              </FeatureGateToolTip>
            )}
            {!isMetricView && canEditProjectsAndCollections && (
              <HexMenuDivider />
            )}
            {canEditProjectsAndCollections && (
              <>
                <div css="margin: 10px 7px 5px 2px">
                  <Heading renderAs="h5">Open in project as...</Heading>
                </div>
                <HexMenuItem
                  icon={<SQLCellIcon />}
                  text={getDataBrowserMenuText("SQL")}
                  onClick={createSqlCell}
                />
                {!isMetricView && (
                  <DataBrowserMenuItems
                    createChartCell={createChartCell}
                    createDisplayTableCell={createDisplayTableCell}
                    createPivotCell={createPivotCell}
                  />
                )}
              </>
            )}
          </HexMenu>
        }
        isOpen={isOpen}
        minimal={true}
        position="bottom-left"
        onInteraction={onPopoverInteraction}
      >
        <HexButton
          active={isOpen}
          className={className}
          icon={<MoreMenuIcon />}
          minimal={true}
          small={true}
          onClick={onButtonClick}
        />
      </HexPopover>
    );
  });

export interface DataBentoSemanticResourceActionsButtonProps {
  className?: string;
  dataConnectionId: DataConnectionId;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  resource: SemanticDatasetListFragment | SemanticViewListFragment;
  connectionType: DataConnectionType;
  additionalMenuItem?: React.ReactNode;
}

export const DataBentoSemanticResourceActionsButton: React.ComponentType<DataBentoSemanticResourceActionsButtonProps> =
  React.memo(function DataBentoSemanticResourceActionsButton({
    additionalMenuItem,
    className,
    dataConnectionId,
    isOpen,
    resource,
    setIsOpen,
  }: DataBentoSemanticResourceActionsButtonProps) {
    const { createExploreCell } = useHomeTableSemanticResourceActions({
      browserType: "HOME_DATA_BENTO",
      tab: "RECENTLY_USED",
      dataConnectionId,
      semanticResource:
        resource.__typename === "SemanticDataset"
          ? {
              __typename: "SemanticDataset",
              id: resource.semanticDatasetId,
              name: resource.semanticDatasetName,
            }
          : {
              __typename: "SemanticView",
              id: resource.semanticViewId,
              name: resource.semanticViewName,
            },
      semanticProjectId: resource.semanticProject.id,
    });

    const { exploreFromScratch: exploreFromScratchFeatureGate } =
      useFeatureGates();

    const createExplore = useCreateExploreFromSemanticResourceCallback();

    const createExploreAndTrack = useCallback(async () => {
      if (resource.__typename === "SemanticDataset") {
        await createExplore(
          { datasetId: resource.semanticDatasetId },
          {
            trackingInfo: {
              browserType: "HOME_DATA_BENTO",
              semanticProjectId: resource.semanticProject.id,
              dataBrowserTab: "RECENTLY_USED",
            },
          },
        );
      } else {
        await createExplore(
          { viewId: resource.semanticViewId },
          {
            trackingInfo: {
              browserType: "HOME_DATA_BENTO",
              semanticProjectId: resource.semanticProject.id,
              dataBrowserTab: "RECENTLY_USED",
            },
          },
        );
      }
    }, [createExplore, resource]);

    const onButtonClick = useCallback(
      (e: React.SyntheticEvent) => {
        setIsOpen(true);
        // prevent triggering data bento row click
        e.stopPropagation();
      },
      [setIsOpen],
    );

    const onPopoverInteraction = useCallback(
      (newIsOpen: boolean, e?: React.SyntheticEvent) => {
        setIsOpen(newIsOpen);
        // prevent event from bubbling up to the parent and
        // triggering data bento row click
        e?.stopPropagation();
      },
      [setIsOpen],
    );

    const orgRole = useCurrentUser()?.orgRole;
    const canEditProjectsAndCollections =
      orgRole !== OrgRole.GUEST && orgRoleHasEditPermissions(orgRole);

    return (
      <HexPopover
        captureDismiss={true}
        content={
          <HexMenu>
            {additionalMenuItem}
            {additionalMenuItem && <HexMenuDivider />}
            <FeatureGateToolTip
              action="explore outside of a published app"
              disabled={exploreFromScratchFeatureGate}
              featureGate="exploreFromScratch"
            >
              <HexMenuItem
                icon={<ExploreIcon />}
                text="Explore"
                onClick={createExploreAndTrack}
              />
            </FeatureGateToolTip>
            {canEditProjectsAndCollections && (
              <HexMenuItem
                icon={<ChartCellIcon />}
                text="Open in project"
                onClick={createExploreCell}
              />
            )}
          </HexMenu>
        }
        isOpen={isOpen}
        minimal={true}
        position="bottom-left"
        onInteraction={onPopoverInteraction}
      >
        <HexButton
          active={isOpen}
          className={className}
          icon={<MoreMenuIcon />}
          minimal={true}
          small={true}
          onClick={onButtonClick}
        />
      </HexPopover>
    );
  });
